import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { intuitColors } from '../chartColors'; // uses .kiwi and .persimmon

export type ShapItem = {
	feature: string; // e.g., "tag:brand=Nintendo"
	shap: number; // positive => increases prediction, negative => decreases
};

type Props = {
	divId: string;
	title?: string;
	data?: ShapItem[];
	topN?: number; // default 10
};

export const shapImpactChart = ({ divId, title, data, topN = 10 }: Props) => {
	const root = am5.Root.new(divId);
	root.setThemes([am5themes_Animated.new(root)]);
	root.interfaceColors.set('text', am5.color(0xffffff));

	const chart = root.container.children.push(
		am5xy.XYChart.new(root, {
			layout: root.verticalLayout,
			panX: false,
			panY: false,
			wheelX: 'none',
			wheelY: 'none',
		})
	);

	chart.children.unshift(
		am5.Label.new(root, {
			text: title ?? 'Top SHAP Contributors',
			fontSize: 18,
			fontWeight: '700',
			x: 0,
			centerX: 0,
			paddingBottom: 8,
			fill: am5.color(0xffffff),
		})
	);

	// Demo data (Consoles) if none provided
	const demo: ShapItem[] = [
		{ feature: 'tag:brand=Nintendo', shap: 0.58 },
		{ feature: 'days_since_launch (Switch 2)', shap: 0.44 },
		{ feature: 'tag:type=console', shap: 0.31 },
		{ feature: 'price_delta_vs_msrp', shap: 0.22 },
		{ feature: 'competing_launch_window', shap: -0.18 },
		{ feature: 'inventory_in_stock_ratio', shap: 0.16 },
		{ feature: 'week_of_year=36 (Labor Day)', shap: 0.11 },
		{ feature: 'marketing_spend_idx', shap: 0.1 },
		{ feature: 'tag:platform=Switch', shap: 0.09 },
		{ feature: 'macro:consumer_confidence', shap: -0.07 },
		{ feature: 'store_supply_chain_delay', shap: -0.06 },
	];

	const rows = (data ?? demo)
		.slice()
		.sort((a, b) => Math.abs(b.shap) - Math.abs(a.shap))
		.slice(0, topN);

	// Axes
	const yAxis = chart.yAxes.push(
		am5xy.CategoryAxis.new(root, {
			categoryField: 'feature',
			renderer: am5xy.AxisRendererY.new(root, { inversed: true, minGridDistance: 12 }),
		})
	);
	yAxis.data.setAll(rows);
	yAxis.get('renderer').labels.template.setAll({ fill: am5.color(0xffffff) });

	// Value axis centered at zero with a bold zero-line
	const xAxis = chart.xAxes.push(
		am5xy.ValueAxis.new(root, {
			renderer: am5xy.AxisRendererX.new(root, {}),
			strictMinMax: true,
			min: Math.min(-Math.max(...rows.map((r) => Math.abs(r.shap))) * 1.15, -0.01),
			max: Math.max(...rows.map((r) => Math.abs(r.shap))) * 1.15,
			numberFormatter: am5.NumberFormatter.new(root, { numberFormat: '#,##0.00' }),
		})
	);
	xAxis.get('renderer').labels.template.setAll({ fill: am5.color(0xffffff) });

	// Zero line
	const zeroRange = xAxis.makeDataItem({ value: 0 });
	xAxis.createAxisRange(zeroRange);
	zeroRange.get('grid')!.setAll({ strokeOpacity: 1, stroke: am5.color(0x6b7a88), strokeWidth: 2 });

	// Series
	const series = chart.series.push(
		am5xy.ColumnSeries.new(root, {
			xAxis,
			yAxis,
			valueXField: 'shap',
			categoryYField: 'feature',
			tooltip: am5.Tooltip.new(root, { labelText: "{feature}: {shap.formatNumber('#,##0.00')}" }),
		})
	);

	// Diverging colors by sign
	series.columns.template.adapters.add('fill', (fill, target) => {
		const v = target.dataItem?.get('valueX') ?? 0;
		return am5.color(v >= 0 ? intuitColors.kiwi : intuitColors.persimmon);
	});
	series.columns.template.adapters.add('stroke', (stroke, target) => {
		const v = target.dataItem?.get('valueX') ?? 0;
		return am5.color(v >= 0 ? intuitColors.kiwi : intuitColors.persimmon);
	});
	series.columns.template.setAll({
		height: am5.p100,
		cornerRadiusTR: 4,
		cornerRadiusBR: 4,
		cornerRadiusTL: 4,
		cornerRadiusBL: 4,
	});

	// Value labels inside bars
	series.bullets.push(() =>
		am5.Bullet.new(root, {
			sprite: am5.Label.new(root, {
				centerY: am5.p50,
				centerX: am5.p50,
				text: "{shap.formatNumber('#,##0.00')}",
				fill: am5.color(0x000000),
				populateText: true,
			}),
			locationX: 0.5,
		})
	);

	series.data.setAll(rows);

	return () => root.dispose();
};
